/**
 * @fileoverview Main controller for the Period Calculator v3.
 * Orchestrates all period tracker modules and handles initialization.
 * @module period-tracker/period-calculator
 */

/* global femcare */

femcare.periodTracker = femcare.periodTracker || {};

/**
 * @namespace femcare.periodTracker.calculator
 * @description Main controller that initializes and coordinates all modules.
 */
femcare.periodTracker.calculator = {
    isInitialized: false,
    elements: {},
    monthNames: null,
    phaseLabels: null,
    dayLabels: null,
    calendarLabel: 'Calendar',

    /**
     * Initializes the period calculator.
     * @param {Object} [options] - Optional configuration overrides
     * @param {boolean} [options.skipRendering] - Skip initial render
     */
    init: function (options) {
        if (this.isInitialized) return;

        options = options || {};

        this.cacheElements();
        this.initJourneyModule();

        if (this.hasCalendarElements()) {
            this.loadTranslations();
            this.loadCycleConfig();
            this.initCalendarModules();
            this.calendarModulesInitialized = true;

            if (!options.skipRendering) {
                this.render();
                this.showWrapper();
            }
        }

        this.isInitialized = true;
    },

    /**
     * Caches DOM elements.
     */
    cacheElements: function () {
        this.elements = {
            wrapper: document.querySelector('.period-calendar__wrapper'),
            circularView: document.querySelector('#circular-view'),
            calendarView: document.querySelector('#calendar-view'),
            container: document.querySelector('.circular-period-calendar'),
            dayNumbers: document.querySelector('.day-numbers'),
            phaseRing: document.querySelector('.phase-ring'),
            separatorRing: document.querySelector('.separator-ring'),
            dayIndicatorRing: document.querySelector('.day-indicator-ring'),
            centerImageContainer: document.querySelector('.center-image'),
            centerImages: document.querySelectorAll('.center-image img'),
            monthDisplay: document.querySelector('#period-current-month'),
            previousMonthButton: document.querySelector('#period-prev-month'),
            nextMonthButton: document.querySelector('#period-next-month'),
            circularViewToggleButton: document.querySelector('#view-toggle-circular'),
            calendarViewToggleButton: document.querySelector('#view-toggle-calendar'),
            calendarGrid: document.querySelector('.calendar-period-view__grid'),
            selectedDayText: document.querySelector('.period-calendar__selected-day-text'),
            selectedDayDate: document.querySelector('.period-calendar__selected-day-date'),
            periodDaysText: document.querySelector('.circular-period-calendar__period-days-text')
        };
    },

    /**
     * Checks if calendar elements are available.
     * @returns {boolean} True if elements ready
     */
    hasCalendarElements: function () {
        return this.elements.container &&
            this.elements.dayNumbers &&
            this.elements.centerImages &&
            this.elements.centerImages.length > 0;
    },

    /**
     * Loads translations from data attributes.
     */
    loadTranslations: function () {
        const wrapper = this.elements.wrapper;
        if (!wrapper) return;

        if (wrapper.dataset.monthNames) {
            try {
                this.monthNames = JSON.parse(wrapper.dataset.monthNames);
            } catch (error) {
                this.monthNames = null;
            }
        }
        if (!this.monthNames) {
            this.monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];
        }

        this.phaseLabels = {
            menstruation: wrapper.dataset.phaseMenstruationLabel || 'Menstruation',
            follicular: wrapper.dataset.phaseFollicularLabel || 'Follicular phase',
            ovulation: wrapper.dataset.phaseOvulationLabel || 'Ovulation',
            luteal: wrapper.dataset.phaseLutealLabel || 'Luteal phase'
        };

        if (wrapper.dataset.dayNames) {
            try {
                this.dayLabels = JSON.parse(wrapper.dataset.dayNames);
            } catch (error) {
                this.dayLabels = null;
            }
        }
        if (!this.dayLabels) {
            this.dayLabels = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        }

        if (wrapper.dataset.calendarLabel) {
            this.calendarLabel = wrapper.dataset.calendarLabel;
        }
    },

    /**
     * Loads cycle configuration from form inputs or journey data.
     */
    loadCycleConfig: function () {
        const config = femcare.periodTracker.config.defaults;
        let menstrualDuration = config.menstrualDuration;
        let menstrualCycle = config.menstrualCycle;
        let lastMenstruationDay = null;

        const durationInput = document.querySelector('#duration');
        const cycleInput = document.querySelector('#cycle');
        const datePicker = document.querySelector('#datepicker');

        if (durationInput && durationInput.value) {
            menstrualDuration = parseInt(durationInput.value, 10);
        }

        if (cycleInput && cycleInput.value) {
            menstrualCycle = parseInt(cycleInput.value, 10);
        }

        if (datePicker && typeof $.fn.datepicker !== 'undefined') {
            try {
                const selectedDate = $(datePicker).datepicker('getDate');
                if (selectedDate) {
                    lastMenstruationDay = new Date(
                        selectedDate.getFullYear(),
                        selectedDate.getMonth(),
                        selectedDate.getDate(),
                        12, 0, 0, 0
                    );
                }
            } catch (error) {
            }
        }

        if (!lastMenstruationDay) {
            const today = new Date();
            lastMenstruationDay = new Date(today.getFullYear(), today.getMonth(), 1, 12, 0, 0, 0);
        }

        femcare.periodTracker.cycleLogic.init({
            menstrualDuration: menstrualDuration,
            menstrualCycle: menstrualCycle,
            lastMenstruationDay: lastMenstruationDay,
            monthNames: this.monthNames
        });

        this.bindConfigChangeListeners();
    },

    /**
     * Binds listeners for configuration input changes.
     */
    bindConfigChangeListeners: function () {
        const durationInput = document.querySelector('#duration');
        const cycleInput = document.querySelector('#cycle');
        const datePicker = document.querySelector('#datepicker');

        const handleConfigChange = () => {
            this.loadCycleConfig();
            this.refresh();
        };

        if (durationInput) {
            durationInput.addEventListener('change', handleConfigChange);
        }

        if (cycleInput) {
            cycleInput.addEventListener('change', handleConfigChange);
        }

        if (datePicker && typeof $.fn.datepicker !== 'undefined') {
            $(datePicker).on('changeDate', handleConfigChange);
        }
    },

    /**
     * Initializes journey module (handles modal, works independently).
     */
    initJourneyModule: function () {
        const journey = femcare.periodTracker.journey;
        journey.init({
            onComplete: (journeyData) => this.handleJourneyComplete(journeyData)
        });
    },

    /**
     * Initializes calendar-related modules (requires DOM elements).
     */
    initCalendarModules: function () {
        const navigation = femcare.periodTracker.navigation;
        const wheel = femcare.periodTracker.wheel;
        const calendar = femcare.periodTracker.calendar;
        const insights = femcare.periodTracker.insights;
        const tooltip = femcare.periodTracker.tooltip;

        // Navigation module
        navigation.init({
            monthDisplay: this.elements.monthDisplay,
            previousMonthButton: this.elements.previousMonthButton,
            nextMonthButton: this.elements.nextMonthButton,
            circularView: this.elements.circularView,
            calendarView: this.elements.calendarView,
            circularViewToggleButton: this.elements.circularViewToggleButton,
            calendarViewToggleButton: this.elements.calendarViewToggleButton,
            monthNames: this.monthNames,
            onMonthChange: (month) => this.handleMonthChange(month),
            onViewChange: (view) => this.handleViewChange(view)
        });

        // Wheel module
        wheel.init({
            container: this.elements.container,
            dayNumbers: this.elements.dayNumbers,
            phaseRing: this.elements.phaseRing,
            separatorRing: this.elements.separatorRing,
            dayIndicatorRing: this.elements.dayIndicatorRing,
            centerImageContainer: this.elements.centerImageContainer,
            centerImages: this.elements.centerImages,
            phaseLabels: this.phaseLabels,
            onDayChange: (day) => this.handleDayChange(day)
        });

        // Calendar module
        if (this.elements.calendarGrid) {
            calendar.init({
                container: this.elements.calendarView,
                gridElement: this.elements.calendarGrid,
                dayLabels: this.dayLabels,
                phaseLabels: this.phaseLabels,
                calendarLabel: this.calendarLabel,
                onDaySelect: (data) => this.handleCalendarDaySelect(data)
            });
        }

        // Insights module
        insights.init();

        // Tooltip module
        tooltip.init({
            circularView: this.elements.circularView,
            wheelContainer: this.elements.container
        });

        // Warning module
        const warning = femcare.periodTracker.warning;
        warning.init({
            circularView: this.elements.circularView,
            calendarView: this.elements.calendarView
        });
    },

    /**
     * Renders all components.
     */
    render: function () {
        const navigation = femcare.periodTracker.navigation;
        const wheel = femcare.periodTracker.wheel;
        const calendar = femcare.periodTracker.calendar;
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const insights = femcare.periodTracker.insights;

        const currentMonth = navigation.getCurrentMonth();
        const totalDays = navigation.getTotalDaysInMonth();
        const phaseData = cycleLogic.calculateMonthPhases(currentMonth, totalDays);

        // Render wheel
        wheel.render(currentMonth, phaseData);

        // Set initial day to today if in current month
        const today = new Date();
        const isCurrentMonth = today.getMonth() === currentMonth.getMonth() &&
                              today.getFullYear() === currentMonth.getFullYear();
        const initialDay = isCurrentMonth ? today.getDate() : 1;

        wheel.setCurrentDay(initialDay, false);

        // Render calendar
        if (calendar.isInitialized) {
            calendar.render(currentMonth, this.monthNames);
            const selectedDate = this.getCurrentSelectedDate();
            calendar.selectDate(selectedDate);
        }

        // Update insights
        const selectedDate = this.getCurrentSelectedDate();
        insights.update(selectedDate);

        // Update selected day text
        this.updateSelectedDayText();
    },

    /**
     * Refreshes all components after config change.
     */
    refresh: function () {
        this.render();
    },

    /**
     * Selects a full date and synchronizes every calendar representation.
     * This must be used for selections that can also change the month.
     * @param {Date} date - Date to select
     */
    selectDate: function (date) {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
            return;
        }

        const navigation = femcare.periodTracker.navigation;
        const wheel = femcare.periodTracker.wheel;
        const calendar = femcare.periodTracker.calendar;
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const insights = femcare.periodTracker.insights;
        const selectedDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            12, 0, 0, 0
        );

        navigation.setCurrentMonth(selectedDate);

        const currentMonth = navigation.getCurrentMonth();
        const totalDays = navigation.getTotalDaysInMonth();
        const phaseData = cycleLogic.calculateMonthPhases(currentMonth, totalDays);

        wheel.render(currentMonth, phaseData);
        wheel.setCurrentDay(selectedDate.getDate(), false);

        if (calendar.isInitialized) {
            calendar.render(currentMonth, this.monthNames);
            calendar.selectDate(selectedDate);
        }

        insights.update(selectedDate);
        this.updateSelectedDayText();
        femcare.periodTracker.warning.evaluateMonthNavigation(currentMonth);
    },

    /**
     * Shows the wrapper element.
     */
    showWrapper: function () {
        if (this.elements.wrapper) {
            this.elements.wrapper.classList.remove('hidden');
        }
    },

    /**
     * Handles month navigation change.
     * @param {Date} month - New month
     */
    handleMonthChange: function (month) {
        const wheel = femcare.periodTracker.wheel;
        const calendar = femcare.periodTracker.calendar;
        const cycleLogic = femcare.periodTracker.cycleLogic;
        const insights = femcare.periodTracker.insights;
        const navigation = femcare.periodTracker.navigation;

        const totalDays = navigation.getTotalDaysInMonth();
        const phaseData = cycleLogic.calculateMonthPhases(month, totalDays);

        // Render wheel for new month
        wheel.render(month, phaseData);

        // Set day to first of month or today if current month
        const today = new Date();
        const isCurrentMonth = today.getMonth() === month.getMonth() &&
                              today.getFullYear() === month.getFullYear();
        const newDay = isCurrentMonth ? today.getDate() : 1;

        wheel.setCurrentDay(newDay, false);

        // Update calendar
        if (calendar.isInitialized) {
            calendar.render(month, this.monthNames);
            const selectedDate = this.getCurrentSelectedDate();
            calendar.selectDate(selectedDate);
        }

        // Update insights
        const selectedDate = this.getCurrentSelectedDate();
        insights.update(selectedDate);

        // Update selected day text
        this.updateSelectedDayText();

        // Check if warning should be shown
        femcare.periodTracker.warning.evaluateMonthNavigation(month);
    },

    /**
     * Handles view switch between circular and calendar.
     * @param {string} view - 'circular' or 'calendar'
     */
    handleViewChange: function (view) {
        // Sync selection between views
        const selectedDate = this.getCurrentSelectedDate();

        if (view === 'calendar') {
            const calendar = femcare.periodTracker.calendar;
            if (calendar.isInitialized) {
                calendar.selectDate(selectedDate);
            }
        }
    },

    /**
     * Handles day change from wheel interaction.
     * @param {number} day - Selected day
     */
    handleDayChange: function (day) {
        const calendar = femcare.periodTracker.calendar;
        const insights = femcare.periodTracker.insights;

        // Sync calendar selection
        if (calendar.isInitialized) {
            const selectedDate = this.getCurrentSelectedDate();
            calendar.selectDate(selectedDate);
        }

        // Update insights
        const selectedDate = this.getCurrentSelectedDate();
        insights.update(selectedDate);

        // Update selected day text
        this.updateSelectedDayText();
    },

    /**
     * Handles day selection from calendar grid.
     * @param {Object} data - Selection data
     */
    handleCalendarDaySelect: function (data) {
        const wheel = femcare.periodTracker.wheel;
        const navigation = femcare.periodTracker.navigation;
        const insights = femcare.periodTracker.insights;

        wheel.setCurrentDay(data.day, false);

        insights.update(data.date);
        this.updateSelectedDayText();
    },

    /** @type {boolean} Calendar modules initialization state */
    calendarModulesInitialized: false,

    /**
     * Handles journey modal completion.
     * @param {Object} journeyData - Collected journey data
     */
    handleJourneyComplete: function (journeyData) {
        // Re-cache elements (results section is now visible)
        this.cacheElements();

        // Initialize calendar modules if not done during init
        if (!this.calendarModulesInitialized && this.hasCalendarElements()) {
            this.loadTranslations();
            this.loadCycleConfig();
            this.initCalendarModules();
            this.calendarModulesInitialized = true;
        }

        // Initialize calendar grid if it wasn't initialized before (element might not have been found)
        const calendar = femcare.periodTracker.calendar;
        if (!calendar.isInitialized && this.elements.calendarGrid) {
            calendar.init({
                container: this.elements.calendarView,
                gridElement: this.elements.calendarGrid,
                dayLabels: this.dayLabels,
                phaseLabels: this.phaseLabels,
                calendarLabel: this.calendarLabel,
                onDaySelect: (data) => this.handleCalendarDaySelect(data)
            });
        }

        // Update cycle config with journey data
        const cycleLogic = femcare.periodTracker.cycleLogic;
        cycleLogic.updateConfig({
            menstrualDuration: journeyData.periodLength,
            menstrualCycle: journeyData.cycleLength,
            lastMenstruationDay: journeyData.lastPeriodDate
        });

        this.showWrapper();
        this.render();
    },

    /**
     * Gets the currently selected full date.
     * @returns {Date} Selected date
     */
    getCurrentSelectedDate: function () {
        const navigation = femcare.periodTracker.navigation;
        const wheel = femcare.periodTracker.wheel;

        const currentMonth = navigation.getCurrentMonth();
        return new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth(),
            wheel.getCurrentDay(),
            12, 0, 0, 0
        );
    },

    /**
     * Updates the selected day date display.
     */
    updateSelectedDayText: function () {
        const selectedDate = this.getCurrentSelectedDate();
        const formattedDate = femcare.shared.dateFormat.formatDayMonth(selectedDate);

        if (this.elements.selectedDayDate) {
            this.elements.selectedDayDate.textContent = formattedDate;
        }

        if (this.elements.periodDaysText) {
            this.elements.periodDaysText.textContent = formattedDate;
        }
    }
};

/**
 * Backward compatibility alias.
 * @deprecated Use femcare.periodTracker.calculator
 */
femcare.form = femcare.form || {};
femcare.form.circularPeriodCalendar = femcare.periodTracker.calculator;
